import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { AssistantModal } from './assistant/AssistantModal';
import { useSettingsContext } from '@/hooks/useSettingsContext';

const AssistantFloatingButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { settings, loading } = useSettingsContext();
  const location = useLocation();

  const handleAssistantClick = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  // Animations du bouton : halo or pulsant, léger flottement, scintillement.
  // IMPORTANT : sur mobile, une règle globale de performance force
  // `* { animation-duration: 0.15s !important }` (useUnifiedPerformance + index.html),
  // ce qui rendait l'animation frénétique. On la surcharge UNIQUEMENT pour ce
  // bouton via des sélecteurs de classe + !important (spécificité supérieure à `*`),
  // sans toucher au plafond de perf des autres éléments.
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes me-halo { 0% { transform: scale(0.85); opacity: 0.45; } 70% { opacity: 0; } 100% { transform: scale(1.75); opacity: 0; } }
      @keyframes me-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
      @keyframes me-twinkle { 0%, 100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.18) rotate(10deg); } }
      .me-fab .me-fab-halo { animation: me-halo 3.8s ease-out infinite !important; }
      .me-fab .me-fab-btn { animation: me-float 4.6s ease-in-out infinite !important; }
      .me-fab .me-fab-spark { animation: me-twinkle 3.8s ease-in-out infinite !important; }
      @media (prefers-reduced-motion: reduce) {
        .me-fab .me-fab-halo, .me-fab .me-fab-btn, .me-fab .me-fab-spark { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const buttonElement = (
    <div
      className="me-fab"
      data-bouton-flottant
      // Au-dessus d'une éventuelle barre basse (useHauteurBarreBasse), pas dessus.
      style={{ position: 'fixed', bottom: 'calc(24px + var(--hauteur-barre-basse, 0px))', right: '24px', zIndex: 9990, pointerEvents: 'auto' }}
    >
      <div
        style={{ position: 'relative', width: '64px', height: '64px', transition: 'transform 0.2s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {/* Halo or pulsant */}
        <span
          className="me-fab-halo"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'rgba(201,169,110,0.4)',
            pointerEvents: 'none',
          }}
        />
        <button
          className="me-fab-btn"
          onClick={handleAssistantClick}
          style={{
            position: 'relative',
            backgroundColor: '#0a2540',
            color: '#C9A96E',
            border: '1.5px solid rgba(201,169,110,0.65)',
            borderRadius: '50%',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(10,37,64,0.45)',
            cursor: 'pointer',
          }}
          aria-label="Ouvrir l'assistant virtuel"
        >
          <Sparkles className="me-fab-spark" size={26} color="#C9A96E" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  // Masqué dans l'admin (outil destiné aux visiteurs), tant que les réglages ne
  // sont pas chargés, ou si l'assistant est désactivé.
  const isAdmin = location.pathname.startsWith('/admin');
  if (loading || !settings?.showVirtualAssistant || isAdmin) {
    return null;
  }

  return (
    <>
      {/* Ne pas afficher le bouton si la modale est ouverte */}
      {!isModalOpen && createPortal(buttonElement, document.body)}
      <AssistantModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};

export default AssistantFloatingButton;
